import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { CatalogBanner } from '@/components/layout/CatalogBanner';
import { Panel } from '@/components/ui';

type Paso = 'correo' | 'codigo' | 'listo';

export function RecuperarClavePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);

  const [paso, setPaso] = useState<Paso>('correo');
  const [correo, setCorreo] = useState(() => params.get('correo') ?? '');
  const [codigo, setCodigo] = useState('');
  const [clave, setClave] = useState('');
  const [clave2, setClave2] = useState('');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const solicitarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const email = correo.trim().toLowerCase();
      const data = await api.authPrepararCuenta(email, 'recuperar');
      const res = await api.authConfirmarCorreo(email, true, 'recuperar');
      if (!res.enviado) {
        setError(res.mensaje ?? 'No se pudo enviar el código. Contacte a ventas@alarcom.mx');
        return;
      }
      setMensaje(res.mensaje ?? `Enviamos un código a ${data.email_enmascarado}`);
      setPaso('codigo');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo enviar el código');
    } finally {
      setLoading(false);
    }
  };

  const reenviarCodigo = async () => {
    const email = correo.trim().toLowerCase();
    if (!email) return;
    setError(null);
    setLoading(true);
    try {
      const res = await api.authConfirmarCorreo(email, true, 'recuperar');
      if (!res.enviado) {
        setError(res.mensaje ?? 'No se pudo reenviar el código');
        return;
      }
      setMensaje(res.mensaje ?? 'Código reenviado. Revisa tu correo.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo reenviar el código');
    } finally {
      setLoading(false);
    }
  };

  const establecerClave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (clave.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (clave !== clave2) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const session = await api.authRestablecerClave(correo.trim().toLowerCase(), codigo.trim(), clave, 'recuperar');
      setSession(session.token, session.cliente);
      setPaso('listo');
      navigate('/cuenta', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CatalogBanner
        title="Crear o recuperar contraseña"
        subtitle="Usa el correo de tu ficha o el que acabas de registrar en la tienda"
      />
      <div className="page-section">
        <div className="container-app max-w-md">
          <Panel title="Recuperar por correo">
            {paso === 'correo' && (
              <form onSubmit={solicitarCodigo} className="space-y-4">
                {error && <Alerta tipo="error" texto={error} />}
                <p className="text-sm text-surface-600">
                  Ingresa el correo que tienes registrado con Alarcom. Si aún no tienes contraseña, aquí
                  podrás crearla.
                </p>
                <div>
                  <label className="mb-1 block text-xs font-medium text-surface-600">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    className="input-field"
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                    placeholder="cliente@empresa.com"
                    required
                    autoComplete="email"
                  />
                </div>
                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? 'Enviando código…' : 'Enviar código al correo'}
                </button>
                <p className="text-center text-xs text-surface-500">
                  <Link to="/ingresar" className="text-brand-700 hover:underline">
                    Volver a iniciar sesión
                  </Link>
                </p>
              </form>
            )}

            {paso === 'codigo' && (
              <form onSubmit={establecerClave} className="space-y-4">
                {mensaje && <Alerta tipo="info" texto={mensaje} />}
                {error && <Alerta tipo="error" texto={error} />}
                <div>
                  <label className="mb-1 block text-xs font-medium text-surface-600">
                    Código de verificación
                  </label>
                  <input
                    className="input-field font-mono tracking-widest"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    inputMode="numeric"
                    maxLength={6}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-surface-600">
                    Nueva contraseña
                  </label>
                  <input
                    type="password"
                    className="input-field"
                    value={clave}
                    onChange={(e) => setClave(e.target.value)}
                    minLength={8}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-surface-600">
                    Confirmar contraseña
                  </label>
                  <input
                    type="password"
                    className="input-field"
                    value={clave2}
                    onChange={(e) => setClave2(e.target.value)}
                    minLength={8}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? 'Guardando…' : 'Guardar y entrar'}
                </button>
                <button
                  type="button"
                  className="btn-secondary w-full"
                  disabled={loading}
                  onClick={reenviarCodigo}
                >
                  Reenviar código
                </button>
              </form>
            )}

            {paso === 'listo' && (
              <Alerta tipo="ok" texto="Contraseña guardada. Redirigiendo a tu cuenta…" />
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}

function Alerta({ tipo, texto }: { tipo: 'error' | 'info' | 'ok'; texto: string }) {
  const clases =
    tipo === 'error'
      ? 'border-red-200 bg-red-50 text-red-700'
      : tipo === 'ok'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : 'border-sky-200 bg-sky-50 text-sky-900';

  return <p className={`rounded-lg border px-3 py-2 text-sm ${clases}`}>{texto}</p>;
}
